import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { mitigationMoistureEvidenceContentTypeSchema } from '../contracts/mitigation-moisture-evidence.contract.js';
export const mitigationMoistureEvidenceConfigurationValueSchema = z.strictObject({
    panelEnabled: z.boolean(),
    maxOfflineAgeDays: z.number().int().min(1).max(90),
    futureClockToleranceMinutes: z.number().int().min(0).max(15),
    maxFileSizeBytes: z.number().int().min(1_024).max(100 * 1_024 * 1_024),
    allowedContentTypes: z.array(mitigationMoistureEvidenceContentTypeSchema).min(1),
    uploadAuthorizationSeconds: z.number().int().min(60).max(7_200),
    listPageSize: z.number().int().min(5).max(100),
    showMeterFields: z.boolean(),
});
export const mitigationMoistureEvidenceConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'mitigation.moisture-evidence.configuration',
        moduleId: 'mitigation',
        title: 'Moisture evidence configuration',
        helpText: 'Controls the narrow moisture-reading capture and private photo policy for this tenant.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelEnabled: true,
            maxOfflineAgeDays: 30,
            futureClockToleranceMinutes: 5,
            maxFileSizeBytes: 25 * 1_024 * 1_024,
            allowedContentTypes: ['image/jpeg', 'image/png', 'image/heic'],
            uploadAuthorizationSeconds: 900,
            listPageSize: 30,
            showMeterFields: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationMoistureEvidenceConfigurationValueSchema,
});
export const mitigationMoistureEvidenceCopyValueSchema = z.strictObject({
    panelTitle: z.string().trim().min(1).max(100),
    panelDescription: z.string().trim().min(1).max(280),
    locationLabel: z.string().trim().min(1).max(80),
    materialLabel: z.string().trim().min(1).max(80),
    readingLabel: z.string().trim().min(1).max(80),
    readingScaleLabel: z.string().trim().min(1).max(80),
    percentScaleLabel: z.string().trim().min(1).max(80),
    relativeScaleLabel: z.string().trim().min(1).max(80),
    occurredAtLabel: z.string().trim().min(1).max(100),
    occurredAtHelp: z.string().trim().min(1).max(240),
    meterNameLabel: z.string().trim().min(1).max(80),
    meterSerialLabel: z.string().trim().min(1).max(80),
    notesLabel: z.string().trim().min(1).max(80),
    photoLabel: z.string().trim().min(1).max(80),
    captureAction: z.string().trim().min(1).max(80),
    retryAction: z.string().trim().min(1).max(80),
    cancelAction: z.string().trim().min(1).max(80),
    hashingStatus: z.string().trim().min(1).max(160),
    uploadingStatus: z.string().trim().min(1).max(160),
    finalizingStatus: z.string().trim().min(1).max(160),
    pendingStatus: z.string().trim().min(1).max(120),
    completeStatus: z.string().trim().min(1).max(120),
    quarantinedStatus: z.string().trim().min(1).max(120),
    deviceTimeLabel: z.string().trim().min(1).max(120),
    receivedTimeLabel: z.string().trim().min(1).max(120),
    emptyLabel: z.string().trim().min(1).max(200),
    loadingLabel: z.string().trim().min(1).max(160),
    loadError: z.string().trim().min(1).max(240),
    captureError: z.string().trim().min(1).max(240),
    occurrenceError: z.string().trim().min(1).max(240),
    fileTypeError: z.string().trim().min(1).max(240),
    fileSizeError: z.string().trim().min(1).max(240),
    verificationError: z.string().trim().min(1).max(240),
    retryFileMismatchError: z.string().trim().min(1).max(240),
});
export const mitigationMoistureEvidenceCopySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.copy.moisture-evidence',
        moduleId: 'mitigation',
        title: 'Moisture evidence interface copy',
        helpText: 'Controls tenant-editable labels for moisture evidence on Project and mobile surfaces.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Moisture evidence',
            panelDescription: 'Capture a moisture reading and its private supporting photo for this Project.',
            locationLabel: 'Location',
            materialLabel: 'Material',
            readingLabel: 'Reading',
            readingScaleLabel: 'Meter scale',
            percentScaleLabel: 'Percent',
            relativeScaleLabel: 'Relative',
            occurredAtLabel: 'Device occurrence time',
            occurredAtHelp: 'This is when the device says the reading occurred. CREST records server receipt separately.',
            meterNameLabel: 'Meter name',
            meterSerialLabel: 'Meter serial number',
            notesLabel: 'Notes',
            photoLabel: 'Evidence photo',
            captureAction: 'Save moisture evidence',
            retryAction: 'Resume interrupted upload',
            cancelAction: 'Cancel transfer',
            hashingStatus: 'Checking evidence photo integrity: {percent}%',
            uploadingStatus: 'Uploading private evidence photo: {percent}%',
            finalizingStatus: 'Verifying and finalizing private evidence…',
            pendingStatus: 'Upload interrupted — evidence is retained for recovery',
            completeStatus: 'Verified',
            quarantinedStatus: 'Photo verification failed',
            deviceTimeLabel: 'Occurred on device',
            receivedTimeLabel: 'Received by CREST',
            emptyLabel: 'No moisture evidence has been recorded for this Project.',
            loadingLabel: 'Loading moisture evidence…',
            loadError: 'Moisture evidence could not be loaded.',
            captureError: 'The moisture evidence could not be saved. The same command can be retried safely.',
            occurrenceError: 'The device occurrence time is outside this tenant’s allowed offline window.',
            fileTypeError: 'This tenant does not allow that evidence photo type.',
            fileSizeError: 'The evidence photo is larger than this tenant allows.',
            verificationError: 'The evidence photo failed integrity or malware verification.',
            retryFileMismatchError: 'Select the exact same evidence photo to resume this interrupted upload.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationMoistureEvidenceCopyValueSchema,
});
export const mitigationMoistureEvidenceSettingRegistrations = [
    mitigationMoistureEvidenceConfigurationSetting,
    mitigationMoistureEvidenceCopySetting,
];
//# sourceMappingURL=mitigation-moisture-evidence-setting-definitions.js.map